/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import {
  ContextItem,
  FhirCastResource,
  FhirHubEvent,
  IMAGINGSTUDY_EVENT
} from '@app/di/channel/fhircast/model/fhircast-resource';
import { Basic, ImagingStudy, OperationOutcome, Patient } from 'fhir/r4';
import { UtilService } from '@app/util/util.service';

import { Injectable } from '@angular/core';
import { WorkItemDataAccessor } from '@app/di/channel/fhircast/model/workitem-data-accessor';
import { StudyDataAccessor } from '@app/di/channel/fhircast/model/study-data-accessor';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';

@Injectable({
  providedIn: 'root'
})
export class FhirBuilderService {
  constructor(private diConfigService: DiConfigService, private utilService: UtilService) {}

  public build(
    workItem: any,
    topic: string,
    hubEvent: FhirHubEvent,
    correlationId?: string,
    outcomeEvent?: FhirHubEvent,
    outcomeId?: string,
    targetApp?: string
  ): FhirCastResource {
    workItem = workItem && this.buildWorkItem(workItem);
    const study: StudyDataAccessor = new WorkItemDataAccessor(workItem);
    const context: ContextItem[] = [];
    const patientId = UtilService.getUUID();
    switch (hubEvent) {
      case FhirHubEvent.IMAGINGSTUDY_OPEN:
      case FhirHubEvent.IMAGINGSTUDY_CLOSE:
        context.push({
          key: 'patient',
          resource: this.buildPatient(patientId, workItem)
        });
        context.push({
          key: 'study',
          resource: this.buildStudy(patientId, study, workItem, hubEvent)
        });
        context.push({
          key: 'basic',
          resource: this.buildBasic(correlationId)
        });
        break;
      case FhirHubEvent.IMAGINGSTUDY_SELECT:
        context.push({
          key: 'patient',
          resource: this.buildPatient(patientId, workItem)
        });
        context.push({
          key: 'study',
          resource: this.buildStudy(patientId, study, workItem, hubEvent)
        });
        break;
      case FhirHubEvent.SYNC_ERROR:
      case FhirHubEvent.SYNC_SUCCESS:
        context.push({
          key: 'operationoutcome',
          resource: this.buildOperationOutcome(outcomeEvent, outcomeId, hubEvent)
        });
        if (correlationId) {
          context.push({
            key: 'basic',
            resource: this.buildBasic(correlationId)
          });
        }
        break;
      case FhirHubEvent.HOME_OPEN:
        if (targetApp) {
          context.push({
            key: 'basic',
            resource: this.buildAppIdBasic(targetApp)
          });
        }
        break;
      case FhirHubEvent.USER_LOGOUT:
      case FhirHubEvent.CLOSE_ALL:
        break;
    }

    return {
      id: UtilService.getUUID(),
      timestamp: new Date(),
      event: {
        'hub.topic': topic,
        'hub.event': hubEvent,
        context: context
      }
    };
  }

  private buildPatient(patientId: string, workItem: any): Patient {
    const identifier = [];
    for (let i = 0; i < workItem.mrn.length; i++) {
      identifier.push({
        system: workItem.mrn[i].system,
        value: workItem.mrn[i].value,
        assigner: {
          display: workItem.mrn[i].display
        }
      });
    }
    return {
      resourceType: 'Patient',
      id: patientId,
      identifier,
      name: [
        {
          text: workItem.patientName.text,
          family: workItem.patientName.family,
          given: [workItem.patientName.given]
        }
      ],
      birthDate: workItem.patientDoB,
      gender: workItem.patientGender
    };
  }

  private buildOperationOutcome(event: string, code: string, operation: 'syncerror' | 'syncsuccess'): OperationOutcome {
    const diagnostics =
      operation === 'syncerror'
        ? `${appConstants.APP_ID.WFM} failed to follow context`
        : `${event} event processed successfully`;

    return {
      resourceType: 'OperationOutcome',
      issue: [
        {
          severity: operation === 'syncerror' ? 'warning' : 'information',
          code: 'processing',
          diagnostics,
          details: {
            coding: [
              {
                system: `${operation}_eventid`,
                code
              }
            ]
          }
        }
      ],
      extension: [
        {
          url: 'appId',
          valueString: appConstants.APP_ID.WFM
        }
      ]
    };
  }

  private buildStudy(patientId: string, study: StudyDataAccessor, workItem: any, event: IMAGINGSTUDY_EVENT) {
    const imagingStudy: ImagingStudy = {
      resourceType: 'ImagingStudy',
      id: UtilService.getUUID(),
      identifier: [
        {
          system: 'urn:dicom:uid',
          value: 'urn:oid:' + study.uid
        }
      ],
      subject: {
        reference: 'Patient/' + patientId
      }, // Mandatory
      status: workItem.studyDataAvailability == 'ONLINE' ? 'available' : 'unknown' // Mandatory
    };
    if (event === FhirHubEvent.IMAGINGSTUDY_OPEN || event === FhirHubEvent.IMAGINGSTUDY_SELECT) {
      imagingStudy.extension = this.getExtensions(workItem);
    }
    return imagingStudy;
  }

  private buildBasic(correlationId?: string): Basic {
    return {
      resourceType: 'Basic',
      id: 'CorrelationID',
      identifier: [
        {
          system: 'urn:uuid',
          value: correlationId ?? UtilService.getUUID()
        }
      ],
      code: {
        text: 'Correlation ID for workflow tracking',
        coding: [
          {
            system: 'correlation_id',
            code: 'correlation_id'
          }
        ]
      }
    };
  }

  private buildAppIdBasic(targetApp: string): Basic {
    return {
      resourceType: 'Basic',
      extension: [
        {
          url: 'appDelivery',
          valueString: JSON.stringify({ to: targetApp, from: appConstants.APP_ID.WFM })
        }
      ],
      code: {
        text: 'Application routing',
        coding: [
          {
            system: 'app_id',
            code: 'app_id'
          }
        ]
      }
    };
  }

  private getExtensions(workItem: any) {
    const fhircast = { ...appConstants.FHIRCAST_DEFAULT_CONFIG, ...this.diConfigService.integrationConfig?.Fhircast };
    const { ImagingStudyResource, ImagingStudySource } = fhircast;
    return [
      {
        url: 'urn:hc.ge.com/pfh/platform/extension/resource:V1',
        valueString: ImagingStudyResource
      },
      {
        url: 'urn:hc.ge.com/pfh/platform/extension/source:V1',
        valueString: ImagingStudySource
      },
      {
        url: 'urn:hc.ge.com/pfh/platform/extension/examIdentifier:V1',
        valueString: `${workItem.requestedProcedureId}`
      }
    ];
  }

  private buildWorkItem = (context: any) => {
    // getTab may return undefined when the tab has already been removed from the
    // tab list (e.g. examTab.closed fires after the tab is spliced). Fall back to
    // the original context object so downstream mapping can still proceed.
    context = this.utilService.getTab(context.id) ?? context;
    if (context?.workItem) {
      context = context.workItem;
    }
    if (context?.keys) {
      context = context.keys;
    }
    context.mrn = context?.mrn?.otherDomainIdentifierList ?? context?.mrn;
    context.requestedProcedureId = context?.requestedProcedureId ?? context?.examId;
    context.patientName = context?.patientNameObj ?? context.patientName;
    return context;
  };
}
